import { useState, useEffect } from 'react'
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import Modal from './Modal'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')

interface Plan {
  id: 'STARTER' | 'GROWTH' | 'PRO'
  name: string
  monthlyPrice: number
}

interface PlanSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  plan: Plan
  userBrandId: string
  isNewCustomer: boolean
  onSuccess: () => void
}

function PaymentForm({
  onSuccess,
  onCancel,
  userBrandId
}: {
  clientSecret: string
  onSuccess: () => void
  onCancel: () => void
  userBrandId: string
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setLoading(true)

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: 'if_required'
    })

    if (error) {
      toast.error(error.message || 'Payment failed')
      setLoading(false)
    } else {
      // Payment confirmed! Now sync the subscription status from Stripe
      try {
        await api.post(`/user-brands/${userBrandId}/subscription/sync`)
        toast.success('Subscription activated successfully!')
        onSuccess()
      } catch (syncError: any) {
        console.error('Failed to sync subscription:', syncError)
        // Payment succeeded but sync failed - user can manually refresh
        toast.success('Payment confirmed! Refreshing subscription status...')
        onSuccess()
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {loading ? 'Processing...' : 'Confirm Payment'}
        </button>
      </div>
    </form>
  )
}

export default function PlanSelectionModal({
  isOpen,
  onClose,
  plan,
  userBrandId,
  onSuccess
}: PlanSelectionModalProps) {
  const [interval] = useState<'monthly' | 'annual'>('monthly')
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [, setSubscriptionId] = useState<string | null>(null)

  // Promo code state
  const [showPromoInput, setShowPromoInput] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount: number; type: 'percent' | 'amount' } | null>(null)
  const [promoError, setPromoError] = useState<string | null>(null)
  const [promoLoading, setPromoLoading] = useState(false)

  const annualPrice = plan.monthlyPrice * 12 * 0.9 // 10% discount
  const basePrice = interval === 'annual' ? annualPrice : plan.monthlyPrice
  const savings = interval === 'annual' ? plan.monthlyPrice * 12 - annualPrice : 0

  // Calculate promo discount
  const promoDiscount = appliedPromo
    ? appliedPromo.type === 'percent'
      ? (basePrice * appliedPromo.discount / 100)
      : appliedPromo.discount
    : 0

  const totalPrice = basePrice - promoDiscount

  const fetchClientSecret = async (billingInterval: 'monthly' | 'annual') => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post(`/user-brands/${userBrandId}/subscription/create`, {
        product: plan.id,
        interval: billingInterval,
        promoCode: appliedPromo?.code
      })

      // Check if payment method was reused (confirmation needed)
      if (response.data.paymentMethodReused) {
        setSubscriptionId(response.data.subscriptionId)
        setShowConfirmation(true)
        setLoading(false)
      } else {
        // Payment required - show payment form
        setClientSecret(response.data.clientSecret)
        setLoading(false)
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to create subscription')
      toast.error(error.response?.data?.message || 'Failed to create subscription')
      setLoading(false)
    }
  }

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code')
      return
    }

    setPromoLoading(true)
    setPromoError(null)

    try {
      const response = await api.post('/stripe/validate-promo-code', {
        promoCode: promoCode.trim(),
        product: plan.id,
        interval: interval
      })

      if (response.data.success && response.data.promotion) {
        const promotion = response.data.promotion
        setAppliedPromo({
          code: promoCode.trim(),
          discount: promotion.discount,
          type: promotion.type
        })
        toast.success(`Promo code "${promoCode.trim()}" applied!`)
        setShowPromoInput(false)
      } else {
        setPromoError('Invalid promo code')
      }
    } catch (error: any) {
      setPromoError(error.response?.data?.message || 'Invalid promo code')
    } finally {
      setPromoLoading(false)
    }
  }

  const removePromoCode = () => {
    setAppliedPromo(null)
    setPromoCode('')
    setPromoError(null)
  }

  const handleConfirmPayment = async () => {
    setLoading(true)
    try {
      // Create subscription with saved payment method
      await api.post(`/user-brands/${userBrandId}/subscription/confirm`, {
        product: plan.id,
        interval: interval,
        promoCode: appliedPromo?.code
      })
      toast.success('Subscription activated successfully!')
      onSuccess()
      onClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to activate subscription'
      const errorType = error.response?.data?.errorType

      // If promo code restriction error, automatically remove the promo code
      if (errorType === 'promo_code_restriction') {
        toast.error(errorMessage)
        removePromoCode()
        toast.info('Promo code removed. Please try again without a promo code.')
      } else {
        toast.error(errorMessage)
      }
      setLoading(false)
    }
  }

  // Fetch client secret on mount
  useEffect(() => {
    if (isOpen) {
      fetchClientSecret(interval)
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>
        <h2 className="text-xl font-semibold text-gray-900">
          {clientSecret ? 'Complete Payment' : 'Subscribe to ' + plan.name}
        </h2>
      </Modal.Header>
      <Modal.Body>
          {/* Billing Interval Toggle - Hidden for now, only monthly billing available
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Billing Cycle
            </label>
            <div className="bg-gray-100 rounded-lg p-1 flex">
              <button
                type="button"
                onClick={() => handleIntervalChange('monthly')}
                disabled={loading}
                className={`flex-1 px-6 py-3 rounded-md text-sm font-medium transition-all disabled:opacity-50 ${
                  interval === 'monthly'
                    ? 'bg-white text-gray-900 shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => handleIntervalChange('annual')}
                disabled={loading}
                className={`flex-1 px-6 py-3 rounded-md text-sm font-medium transition-all disabled:opacity-50 ${
                  interval === 'annual'
                    ? 'bg-white text-gray-900 shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>Annual</span>
                <span className="text-green-600 ml-2 text-xs font-bold">Save 10%</span>
              </button>
            </div>
          </div>
          */}

          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Order Summary
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">Updating pricing...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{plan.name} Plan</span>
                    <span className="font-medium text-gray-900">
                      ${plan.monthlyPrice}/month
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Billing</span>
                    <span className="font-medium text-gray-900">
                      {interval === 'annual' ? 'Annual' : 'Monthly'}
                    </span>
                  </div>

                  {interval === 'annual' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600 font-medium">Annual Discount (10%)</span>
                      <span className="font-medium text-green-600">
                        -${savings.toFixed(0)}
                      </span>
                    </div>
                  )}

                  {/* Promo Code Section */}
                  {!appliedPromo && !showPromoInput && (
                    <div className="pt-2 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setShowPromoInput(true)}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        + Add promo code
                      </button>
                    </div>
                  )}

                  {showPromoInput && !appliedPromo && (
                    <div className="pt-2 border-t border-gray-200 space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          onKeyDown={(e) => e.key === 'Enter' && applyPromoCode()}
                          placeholder="Enter promo code"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          disabled={promoLoading}
                        />
                        <button
                          type="button"
                          onClick={applyPromoCode}
                          disabled={promoLoading || !promoCode.trim()}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {promoLoading ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                      {promoError && (
                        <p className="text-xs text-red-600">{promoError}</p>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setShowPromoInput(false)
                          setPromoCode('')
                          setPromoError(null)
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {appliedPromo && (
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 font-medium">
                          Promo: {appliedPromo.code}
                        </span>
                        <button
                          type="button"
                          onClick={removePromoCode}
                          className="text-xs text-gray-500 hover:text-red-600 underline"
                        >
                          Remove
                        </button>
                      </div>
                      <span className="font-medium text-green-600">
                        -${promoDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-3 flex justify-between items-baseline">
                  <span className="text-sm font-semibold text-gray-900">
                    {interval === 'annual' ? 'Due Today' : 'First Payment'}
                  </span>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-indigo-600">
                      ${totalPrice.toFixed(2)}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Payment Form or Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-3" />
                <p className="text-sm text-gray-600">Preparing payment form...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error}</p>
                  <button
                    onClick={() => fetchClientSecret(interval)}
                    className="mt-2 text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Screen - Show when payment method exists */}
          {showConfirmation && !loading && (
            <div className="space-y-6">
              {/* Success Message */}
              <div className="flex items-start p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">Payment method found</p>
                  <p className="text-sm text-green-700 mt-1">
                    We'll use your saved payment method to complete this subscription.
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      setShowConfirmation(false)
                      setLoading(true)
                      // Force new payment method by requesting client secret without reuse
                      try {
                        const response = await api.post(`/user-brands/${userBrandId}/subscription/create`, {
                          product: plan.id,
                          interval: interval,
                          forceNewPaymentMethod: true
                        })
                        setClientSecret(response.data.clientSecret)
                        setLoading(false)
                      } catch (error: any) {
                        setError(error.response?.data?.message || 'Failed to load payment form')
                        toast.error('Failed to load payment form')
                        setLoading(false)
                      }
                    }}
                    className="mt-2 text-sm font-medium text-green-700 hover:text-green-800 underline"
                  >
                    Use a different payment method
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPayment}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Confirm Payment'}
                </button>
              </div>
            </div>
          )}

          {/* Payment Form */}
          {clientSecret && !loading && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#4f46e5',
                    borderRadius: '8px'
                  }
                }
              }}
            >
              <PaymentForm
                clientSecret={clientSecret}
                userBrandId={userBrandId}
                onSuccess={() => {
                  setClientSecret(null)
                  onSuccess()
                  onClose()
                }}
                onCancel={() => {
                  setClientSecret(null)
                  setLoading(false)
                }}
              />
            </Elements>
          )}
      </Modal.Body>
    </Modal>
  )
}
